import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AlignLeft, Check, Rss } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { useTickerStore, type TickerTrouble } from '@/app/store/ticker'
import { useMe } from '@/features/auth/hooks/useAuth'
import {
    DEFAULT_TICKER_PREFS,
    TICKER_DWELLS,
    TICKER_KINDS,
    TICKER_LEVELS,
    TICKER_MAX_SETS,
    type TickerDwell,
    type TickerLevel,
    type TickerPrefs,
} from '../api'
import { dwellFor } from '../kinds'
import {
    userCanViewTicker,
    useFollowOpenedSets,
    useSaveTickerPrefs,
    useTickerAccessRecheck,
    useTickerConnection,
    useTickerPermission,
    useTickerPrefs,
    useTickerRecoverySets,
} from '../hooks'
import { KindChip } from './KindChip'
import { NotificationsPanel } from './NotificationsPanel'

const SECTION = 'border-b-[0.5px] border-border px-3.5 py-3'

/** The setting's effect, in seconds, short class to long class: "1.6–3.1s" at quick. */
function dwellRange(dwell: TickerDwell, lang: string): string {
    const seconds = (ms: number) => ms / 1000
    try {
        // Whole seconds: the dwell times are whole seconds (DWELL_MS), so this rounds nothing away.
        const n = new Intl.NumberFormat(lang, { maximumFractionDigits: 0 })
        const unit = new Intl.NumberFormat(lang, { style: 'unit', unit: 'second', unitDisplay: 'narrow', maximumFractionDigits: 0 })
        return `${n.format(seconds(dwellFor('task_complete', dwell)))}–${unit.format(seconds(dwellFor('task_issue', dwell)))}`
    } catch {
        return `${seconds(dwellFor('task_complete', dwell))}–${seconds(dwellFor('task_issue', dwell))}s`
    }
}
const SECTION_HEADER = 'mb-[7px] text-[11px] uppercase tracking-[.05em] text-muted-foreground'

/**
 * What a silent ticker says about itself, by the store's `watchError`. A refusal of the permission
 * hides the whole ticker, so it has no line here.
 */
const TROUBLE_KEY: Partial<Record<TickerTrouble, string>> = {
    unavailable: 'ticker.cannotConnect',
    invalid: 'ticker.cannotConnect',
    unauthenticated: 'ticker.signInAgain',
}
/** Keep the menu open while its settings are changed; only "See all messages" closes it. */
const keepOpen = (e: Event) => e.preventDefault()

/**
 * The ticker's taskbar icon and its dropdown (docs/ticker-design.md §9): Show ticker, Detail level,
 * Time on screen (the dwell, per user), Recovery sets (the sets this user receives messages from),
 * Message colours, See all messages.
 *
 * This is also where the ticker is wired up: it holds the socket connection and the history panel,
 * because it is the one ticker surface that is always mounted for a user who may see the ticker.
 * Renders nothing without `ticker.view`, nothing while the server refuses the ticker (a 403 on
 * any ticker read, or a watch refused for the permission), and nothing until this user's
 * preferences have been read. While a 403 hides it, it re-asks now and then (useTickerAccessRecheck).
 */
export function TickerMenu() {
    const allowed = useTickerPermission()
    const { data: me } = useMe()
    const prefsQuery = useTickerPrefs(allowed)
    useTickerConnection(allowed)
    useTickerAccessRecheck(userCanViewTicker(me))
    if (!allowed || !prefsQuery.data) return null
    return (
        <>
            <TickerDropdown />
            <NotificationsPanel />
        </>
    )
}

function TickerDropdown() {
    const { t, i18n } = useTranslation()
    const prefs = useTickerStore((s) => s.prefs)
    const unseen = useTickerStore((s) => s.unseen)
    const watchError = useTickerStore((s) => s.watchError)
    const menuOpen = useTickerStore((s) => s.menuOpen)
    const setMenuOpen = useTickerStore((s) => s.setMenuOpen)
    const openPanel = useTickerStore((s) => s.openPanel)
    const save = useSaveTickerPrefs()
    const current: TickerPrefs = prefs ?? DEFAULT_TICKER_PREFS
    const allSets = !!current.allSets
    const sets = useTickerRecoverySets(true, { poll: allSets })
    useFollowOpenedSets(sets.data)

    // The server lists every named set; only open ones are offered. A chosen set that has since
    // closed is dropped by the server on the next read or save of the preferences.
    const openSets = useMemo(() => (sets.data ?? []).filter((set) => set.open === true), [sets.data])
    const on = !!prefs?.enabled
    const troubleKey = on && watchError ? TROUBLE_KEY[watchError] : undefined
    const update = (patch: Partial<TickerPrefs>) => save.mutate({ ...current, ...patch })
    const sorted = (ids: Iterable<number>) => Array.from(ids).sort((a, b) => a - b)
    // All recovery sets (Andrew, 29 Sep 2026). Unticking one set while on All switches to choosing
    // sets one by one: the list becomes every open set but that one. Ticking All returns to All;
    // unticking it chooses none. The store holds the open sets at once, so the ticker does not go
    // quiet while the save is on its way; the save itself sends no list (api.ts).
    const toggleSet = (id: number) => {
        const chosen = new Set(allSets ? openSets.map((set) => set.id).slice(0, TICKER_MAX_SETS) : current.recoverySetIds)
        if (chosen.has(id)) chosen.delete(id)
        else chosen.add(id)
        update({ allSets: false, recoverySetIds: sorted(chosen) })
    }
    const toggleAll = () =>
        update(allSets ? { allSets: false, recoverySetIds: [] } : { allSets: true, recoverySetIds: sorted(openSets.map((set) => set.id).slice(0, TICKER_MAX_SETS)) })

    return (
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
                {/* Sized and coloured like its neighbours (theme, density, timezone): a 36px ghost
                    button, a 16px icon in the text colour. Whether the ticker is live is the green
                    dot's job, not the icon's colour. */}
                <Button
                    variant="ghost"
                    size="icon"
                    className="relative h-9 w-9"
                    title={t('ticker.buttonLabel')}
                    aria-label={unseen > 0 ? t('ticker.buttonLabelUnseen', { count: unseen }) : t('ticker.buttonLabel')}
                    data-state-on={on ? 'true' : 'false'}
                    data-ticker-trigger
                >
                    <Rss className="h-4 w-4" />
                    {on && unseen === 0 && (
                        <span aria-hidden className="absolute right-1.5 top-1.5 h-[7px] w-[7px] rounded-full bg-[#1D9E75] ring-[1.5px] ring-background" />
                    )}
                    {unseen > 0 && (
                        <span
                            data-testid="ticker-unseen"
                            className="absolute right-0 top-0 flex h-[14px] min-w-[14px] items-center justify-center rounded-full bg-primary px-0.5 text-[9px] font-semibold leading-none text-primary-foreground ring-2 ring-background"
                        >
                            {unseen > 9 ? '9+' : unseen}
                        </span>
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                className="w-[330px] max-w-[calc(100vw-16px)] overflow-hidden rounded-xl p-0"
                // "See all messages" opens the panel, which takes the focus; the menu must not
                // hand it back to its trigger as it closes.
                onCloseAutoFocus={(e) => {
                    if (useTickerStore.getState().panel.open) e.preventDefault()
                }}
            >
                {/* Show ticker */}
                <div className={SECTION}>
                    <DropdownMenuItem
                        role="menuitemcheckbox"
                        aria-checked={on}
                        onSelect={(e) => {
                            keepOpen(e)
                            update({ enabled: !on })
                        }}
                        className="flex items-center justify-between rounded-md px-1 py-1"
                    >
                        <span className="flex items-center gap-[9px] text-[13px] font-semibold">
                            <Rss className="h-4 w-4" aria-hidden />
                            {t('ticker.showTicker')}
                        </span>
                        <Switch checked={on} tabIndex={-1} aria-hidden className="pointer-events-none" />
                    </DropdownMenuItem>
                    {troubleKey && (
                        <p role="status" data-testid="ticker-trouble" className="mt-1.5 px-1 text-[11.5px] text-destructive">
                            {t(troubleKey)}
                        </p>
                    )}
                </div>

                {/* Detail level */}
                <div className={SECTION} role="group" aria-label={t('ticker.detailLevel')}>
                    <p className={SECTION_HEADER}>{t('ticker.detailLevel')}</p>
                    {TICKER_LEVELS.map((level: TickerLevel) => {
                        const selected = current.level === level
                        return (
                            <DropdownMenuItem
                                key={level}
                                role="menuitemradio"
                                aria-checked={selected}
                                disabled={!on}
                                onSelect={(e) => {
                                    keepOpen(e)
                                    if (!selected) update({ level })
                                }}
                                className={cn(
                                    'flex items-start gap-2.5 rounded-lg border-[0.5px] border-transparent px-[9px] py-2',
                                    selected && 'border-primary/25 bg-primary/5',
                                    !on && 'opacity-45',
                                )}
                            >
                                <span
                                    aria-hidden
                                    className={cn(
                                        'relative mt-px h-4 w-4 flex-shrink-0 rounded-full border-[1.6px]',
                                        selected ? 'border-primary' : 'border-muted-foreground/50',
                                    )}
                                >
                                    {selected && <span className="absolute inset-[3px] rounded-full bg-primary" />}
                                </span>
                                <span>
                                    <span className="block text-[13px] font-semibold">{t(`ticker.levels.${level}.title`)}</span>
                                    <span className="mt-px block text-[11px] leading-[1.3] text-muted-foreground">
                                        {t(`ticker.levels.${level}.description`)}
                                    </span>
                                </span>
                            </DropdownMenuItem>
                        )
                    })}
                </div>

                {/* Time on screen: how long each card stays on the bar */}
                <div className={SECTION} role="group" aria-label={t('ticker.timeOnScreen')}>
                    <p className={SECTION_HEADER}>{t('ticker.timeOnScreen')}</p>
                    <div className="flex gap-1 rounded-lg border-[0.5px] bg-muted/40 p-0.5">
                        {TICKER_DWELLS.map((dwell: TickerDwell) => {
                            const selected = current.dwell === dwell
                            return (
                                <DropdownMenuItem
                                    key={dwell}
                                    role="menuitemradio"
                                    aria-checked={selected}
                                    disabled={!on}
                                    onSelect={(e) => {
                                        keepOpen(e)
                                        if (!selected) update({ dwell })
                                    }}
                                    className={cn(
                                        'flex flex-1 flex-col items-center justify-center gap-0 rounded-md px-1.5 py-1 text-center',
                                        selected ? 'bg-background shadow-sm' : 'text-muted-foreground',
                                        !on && 'opacity-45',
                                    )}
                                >
                                    <span className="text-[12.5px] font-semibold">{t(`ticker.dwell.${dwell}`)}</span>
                                    <span className="text-[10.5px] text-muted-foreground" aria-hidden>
                                        {dwellRange(dwell, i18n.language)}
                                    </span>
                                </DropdownMenuItem>
                            )
                        })}
                    </div>
                    <p className="mt-1.5 text-[11px] text-muted-foreground">{t('ticker.timeOnScreenHint')}</p>
                </div>

                {/* Recovery sets: the sets this user receives messages from */}
                <div className={SECTION} role="group" aria-label={t('ticker.recoverySets')}>
                    <p className={SECTION_HEADER}>{t('ticker.recoverySets')}</p>
                    <p className="mb-1.5 text-[11px] text-muted-foreground">{t('ticker.recoverySetsHint')}</p>
                    {!sets.isLoading && !sets.isError && (
                        <SetCheckbox
                            checked={allSets}
                            label={t('ticker.allRecoverySets')}
                            hint={t('ticker.allRecoverySetsHint')}
                            onToggle={toggleAll}
                            testId="ticker-all-sets"
                        />
                    )}
                    <div className="max-h-44 overflow-y-auto">
                        {sets.isLoading ? (
                            <p className="px-1 py-1.5 text-[12px] text-muted-foreground">{t('ticker.loading')}</p>
                        ) : sets.isError ? (
                            <p className="px-1 py-1.5 text-[12px] text-destructive">{t('ticker.recoverySetsError')}</p>
                        ) : !openSets.length ? (
                            <p className="px-1 py-1.5 text-[12px] text-muted-foreground">{t('ticker.recoverySetsEmpty')}</p>
                        ) : (
                            // On All every open set shows ticked: each is being received.
                            openSets.map((set) => (
                                <SetCheckbox
                                    key={set.id}
                                    checked={allSets || current.recoverySetIds.includes(set.id)}
                                    label={set.description}
                                    onToggle={() => toggleSet(set.id)}
                                />
                            ))
                        )}
                    </div>
                </div>

                {/* Message colours */}
                <div className={SECTION}>
                    <p className={SECTION_HEADER}>{t('ticker.messageColours')}</p>
                    <div className="flex flex-col gap-1.5">
                        {TICKER_KINDS.map((kind) => (
                            <div key={kind} className="grid grid-cols-[92px_1fr] items-center gap-x-2.5">
                                <KindChip kind={kind} size="legend" />
                                <span className="text-[11.5px] text-muted-foreground">{t(`ticker.kinds.${kind}.legend`)}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* See all messages */}
                <DropdownMenuItem
                    onSelect={() => openPanel(null)}
                    className="flex items-center gap-[9px] rounded-none px-3.5 py-[11px] text-[13px] font-semibold text-primary"
                >
                    <AlignLeft className="h-4 w-4" aria-hidden />
                    {t('ticker.seeAll')}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

/** One tickable line of the Recovery sets section: All recovery sets, or one set. */
function SetCheckbox({
    checked,
    label,
    hint,
    onToggle,
    testId,
}: {
    checked: boolean
    label: string
    hint?: string
    onToggle: () => void
    testId?: string
}) {
    return (
        <DropdownMenuItem
            role="menuitemcheckbox"
            aria-checked={checked}
            data-testid={testId}
            onSelect={(e) => {
                keepOpen(e)
                onToggle()
            }}
            className={cn('flex gap-2.5 rounded-md px-[9px] py-1.5 text-[12.5px]', hint ? 'items-start' : 'items-center')}
        >
            <span
                aria-hidden
                className={cn(
                    'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-[4px] border-[1.5px]',
                    hint && 'mt-px',
                    checked ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/50',
                )}
            >
                {checked && <Check className="h-3 w-3" strokeWidth={3} />}
            </span>
            {hint ? (
                <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold">{label}</span>
                    <span className="mt-px block text-[11px] leading-[1.3] text-muted-foreground">{hint}</span>
                </span>
            ) : (
                <span className="min-w-0 flex-1 truncate">{label}</span>
            )}
        </DropdownMenuItem>
    )
}
